import type { Filter, FilterDataSwitch } from '@/app/common/filters/core/filtersTypes.ts';
import { FILTER_TYPE } from '@/app/common/filters/core/filtersTypes.ts';
import { SearchModes } from '@/domainLogic/index.ts';
import type { SearchInfoData } from '@/app/search/types.ts';
import type { DynamicFiltersResponse } from '@/app/components/search/utils/api/dynamicFiltersResponseTypes.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import type { Localization } from '@/app/components/search/common/Localization.ts';
import type { TranslationDictionary } from '@/app/components/translations/createTranslationDictionary.ts';
import { getTranslationServer } from '@/app/components/translations/createTranslationDictionary.ts';
import { FILTER_NAME } from '@/app/common/filters/core/filterNames.ts';
import { bookNowPayLaterAvailable, freeCancellationWithdrawn } from '@/app/common/filters/core/filterFlags.ts';
import { removePlaceholders } from '@/app/components/search/utils/translationNodes.tsx';

type CreateBookingOptionsFilters = {
  searchParams: SearchParams;
  searchInfoData: SearchInfoData;
  localization: Localization;
  dynamicFiltersResponse: DynamicFiltersResponse | undefined;
  translationDictionary: TranslationDictionary;
};
export const createBookingOptionsFilters = ({
  searchParams,
  searchInfoData,
  localization,
  dynamicFiltersResponse,
  translationDictionary,
}: CreateBookingOptionsFilters): Filter[] => {
  const { searchMode } = searchInfoData;

  // FREE CANCELLATION
  const showFreeCancellation =
    !freeCancellationWithdrawn(localization?.configValuesApp) &&
    (searchInfoData.searchMode === SearchModes.HO ||
      searchParams[URLParameters.FREE_CANCELLATION] ||
      dynamicFiltersResponse?.freeCancellation !== 0);
  const freeCancellationBaseLabel =
    searchMode === SearchModes.HO
      ? getTranslationServer('vacation.oss.filters.free_cancellation.text', translationDictionary)
      : getTranslationServer('vacation.oss.filters.free_cancellation.text.dp', translationDictionary);
  const freeCancellationLabel = removePlaceholders(freeCancellationBaseLabel);
  const freeCancellationFilter: Filter | undefined = showFreeCancellation
    ? {
        name: FILTER_NAME.FREE_CANCELLATION,
        type: FILTER_TYPE.SWITCH,
        disabled: dynamicFiltersResponse?.freeCancellation === 0,
        data: {
          label: freeCancellationLabel,
          labelEmphasis: freeCancellationBaseLabel,
          selected: searchParams?.[URLParameters.FREE_CANCELLATION] === '1',
          counter: dynamicFiltersResponse?.freeCancellation,
        } as FilterDataSwitch,
      }
    : undefined;

  // BOOK NOW PAY LATER: an applied filter always keeps its control, so it can be seen and cleared.
  const bookNowPayLaterApplied = !!searchParams[URLParameters.BOOK_NOW_PAY_LATER];
  const showBookNowPayLater =
    bookNowPayLaterApplied ||
    (bookNowPayLaterAvailable({ config: localization?.configValuesApp, searchMode, searchParams }) &&
      dynamicFiltersResponse?.bookNowPayLater !== 0);
  const bookNowPayLaterLabel = localization?.configValuesApp?.depositAmountEnabled
    ? getTranslationServer('vacation.oss.filters.book_now_pay_later_amount.text', translationDictionary)
    : getTranslationServer('vacation.oss.filters.book_now_pay_later.text', translationDictionary);
  const bookNowPayLaterFilter: Filter | undefined = showBookNowPayLater
    ? {
        name: FILTER_NAME.BOOK_NOW_PAY_LATER,
        type: FILTER_TYPE.SWITCH,
        disabled: !bookNowPayLaterApplied && dynamicFiltersResponse?.bookNowPayLater === 0,
        data: {
          label: bookNowPayLaterLabel,
          selected: searchParams?.[URLParameters.BOOK_NOW_PAY_LATER] === '1',
          counter: dynamicFiltersResponse?.bookNowPayLater,
        } as FilterDataSwitch,
      }
    : undefined;

  return [...[freeCancellationFilter], ...[bookNowPayLaterFilter]].filter((f) => !!f);
};
